import { useState, type FormEvent } from 'react';
import { Modal } from '../Modal';
import { ApiError } from '../../lib/api';
import { useCreateCustomTask, type CustomTaskInput } from '../../lib/tasks';

const CADENCES: CustomTaskInput['cadence'][] = ['DAILY', 'WEEKLY', 'MONTHLY'];

export function CustomTaskFormModal({ onClose }: { onClose: () => void }) {
  const createCustomTask = useCreateCustomTask();
  const [name, setName] = useState('');
  const [cadence, setCadence] = useState<CustomTaskInput['cadence']>('DAILY');
  const [scope, setScope] = useState<CustomTaskInput['scope']>('CHARACTER');
  const [maxCount, setMaxCount] = useState('1');
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    const parsedMax = Number(maxCount);
    if (!Number.isInteger(parsedMax) || parsedMax < 1 || parsedMax > 999) {
      setError('Max count must be between 1 and 999.');
      return;
    }
    createCustomTask.mutate(
      { name: name.trim(), cadence, scope, maxCount: parsedMax },
      {
        onSuccess: onClose,
        onError: (err) => {
          setError(err instanceof ApiError ? err.serverMessage : 'Could not create this task.');
        },
      },
    );
  };

  return (
    <Modal title="New custom task" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="custom-name" className="mb-1 block text-sm font-medium text-text-muted">
            Name
          </label>
          <input
            id="custom-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g. Guild flag captures"
            className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="custom-cadence" className="mb-1 block text-sm font-medium text-text-muted">
              Cadence
            </label>
            <select
              id="custom-cadence"
              value={cadence}
              onChange={(e) => setCadence(e.target.value as CustomTaskInput['cadence'])}
              className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text focus:border-accent focus:outline-none"
            >
              {CADENCES.map((c) => (
                <option key={c} value={c}>
                  {c.charAt(0) + c.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="custom-scope" className="mb-1 block text-sm font-medium text-text-muted">
              Tracked per
            </label>
            <select
              id="custom-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as CustomTaskInput['scope'])}
              className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text focus:border-accent focus:outline-none"
            >
              <option value="CHARACTER">Character</option>
              <option value="ACCOUNT">Account</option>
              <option value="WORLD">Account (world-shared)</option>
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="custom-max" className="mb-1 block text-sm font-medium text-text-muted">
            Max count <span className="text-text-faint">(1 for a plain checkbox)</span>
          </label>
          <input
            id="custom-max"
            type="number"
            inputMode="numeric"
            min={1}
            max={999}
            value={maxCount}
            onChange={(e) => setMaxCount(e.target.value)}
            className="min-h-tap w-32 rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text focus:border-accent focus:outline-none"
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap rounded-lg px-4 text-sm font-semibold text-text-muted hover:bg-bg-elevated-hover"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createCustomTask.isPending}
            className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong disabled:opacity-50"
          >
            {createCustomTask.isPending ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
